import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useMemo, type ReactElement } from 'react'
import { z } from 'zod'

import { DataTablePagination } from '~/components/data/DataTablePagination'
import {
  TicketsKanban,
  TicketsTable,
  ViewToggle,
  supportKeys,
  supportTicketsListQuery,
  useBulkSetTicketStatus,
  useSetTicketStatus,
  type SupportTicketRow,
  type SupportView,
  type TicketStatus,
} from '~/features/support'
import { http } from '~/lib/http'
import {
  toPaginatedResult,
  usePaginatedQuery,
  type PaginatedParams,
  type PaginatedResult,
} from '~/lib/query/use-paginated-query'
import { useUrlState } from '~/lib/query/use-url-state'
import { logger } from '~/lib/logger'

/**
 * `/admin/support` — list + Kanban toggle.
 *
 * URL search keys: `view`, `status`, `priority`, `page`, `limit`.
 *
 * The table view is URL-paginated via `usePaginatedQuery`; the Kanban
 * keeps its existing "all open tickets at once" behavior because
 * pagination doesn't compose with drag-and-drop status moves.
 *
 * Spec: 10-support.md §C.
 */
const supportSearchSchema = z.object({
  view: z.enum(['table', 'kanban']).catch('table'),
  status: z.string().optional(),
  priority: z.string().optional(),
  page: z.coerce.number().int().min(1).catch(1),
  limit: z.coerce.number().int().min(10).max(100).catch(25),
})
type SupportSearch = z.infer<typeof supportSearchSchema>

const URL_DEFAULTS: SupportSearch = {
  view: 'table',
  page: 1,
  limit: 25,
}

export const Route = createFileRoute('/admin/support/')({
  validateSearch: supportSearchSchema,
  component: SupportListPage,
})

interface TicketsListPayload {
  readonly tickets?: readonly SupportTicketRow[]
  readonly items?: readonly SupportTicketRow[]
  readonly total?: number
  readonly pagination?: {
    readonly page?: number
    readonly limit?: number
    readonly total?: number
    readonly totalPages?: number
  }
}

async function fetchTicketsPage(
  params: PaginatedParams,
): Promise<PaginatedResult<SupportTicketRow>> {
  const query: Record<string, string | number | undefined> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.search) query.search = params.search
  if (params.sort) query.sort = params.sort
  if (params.filters?.status) query.status = params.filters.status
  if (params.filters?.priority) query.priority = params.filters.priority

  const raw = await http.get<TicketsListPayload>(
    '/api/v1/admin/support/tickets',
    { query },
  )
  const rows = (raw.tickets ?? raw.items ?? []) as readonly SupportTicketRow[]
  const total = raw.pagination?.total ?? raw.total ?? rows.length
  return toPaginatedResult(rows, total, params, raw.pagination)
}

function SupportListPage(): ReactElement {
  const search = Route.useSearch()
  const [, setUrl] = useUrlState(supportSearchSchema, URL_DEFAULTS)

  // ---- TABLE: paginated query -----------------------------------------
  const tableParams: PaginatedParams = {
    page: search.page,
    limit: search.limit,
    filters: {
      ...(search.status ? { status: search.status } : {}),
      ...(search.priority ? { priority: search.priority } : {}),
    },
  }
  const tableQuery = usePaginatedQuery<SupportTicketRow>({
    queryKey: [...supportKeys.all, 'list-paginated'],
    params: tableParams,
    fetcher: fetchTicketsPage,
    enabled: search.view === 'table',
  })

  // ---- KANBAN: keep the existing non-paginated query ------------------
  const kanbanFilters = useMemo(() => {
    const out: { status?: string; priority?: string } = {}
    if (search.status !== undefined) out.status = search.status
    if (search.priority !== undefined) out.priority = search.priority
    return out
  }, [search.status, search.priority])
  const kanbanQuery = useQuery({
    ...supportTicketsListQuery(kanbanFilters),
    enabled: search.view === 'kanban',
  })

  const setStatus = useSetTicketStatus()
  const bulkSetStatus = useBulkSetTicketStatus()

  const setView = (next: SupportView): void => setUrl({ view: next })
  const setStatusFilter = (next: string | undefined): void =>
    setUrl({ status: next, page: 1 })
  const setPriorityFilter = (next: string | undefined): void =>
    setUrl({ priority: next, page: 1 })

  const handleKanbanMove = (ticketId: string, status: TicketStatus): void => {
    setStatus.mutate(
      { ticketId, status },
      {
        onError: (cause) => {
          logger.warn('support: status update failed', {
            ticketId,
            status,
            cause,
          })
        },
      },
    )
  }

  const handleBulkAction = (
    action: 'assign' | 'priority' | 'status' | 'close',
    ticketIds: readonly string[],
  ): void => {
    if (action === 'close') {
      bulkSetStatus.mutate(
        { ticketIds, status: 'closed' },
        {
          onError: (cause) =>
            logger.warn('support: bulk close failed', { ticketIds, cause }),
        },
      )
    }
    if (action !== 'close') {
      logger.info('support: bulk action requested', { action, ticketIds })
    }
  }

  const tableRows = useMemo(
    () => tableQuery.data?.data ?? [],
    [tableQuery.data],
  )
  const tablePagination = tableQuery.data?.pagination
  const tableTotal = tablePagination?.total ?? 0

  const kanbanTickets = useMemo(
    () => kanbanQuery.data?.tickets ?? [],
    [kanbanQuery.data],
  )

  const counts = useMemo(() => {
    const source = search.view === 'kanban' ? kanbanTickets : tableRows
    let open = 0
    let inProgress = 0
    let waiting = 0
    let unassigned = 0
    for (const t of source) {
      if (t.status === 'open') open += 1
      if (t.status === 'in_progress') inProgress += 1
      if (t.status === 'waiting_on_customer') waiting += 1
      if (!t.assigned_to) unassigned += 1
    }
    return { open, inProgress, waiting, unassigned }
  }, [kanbanTickets, tableRows, search.view])

  const view: SupportView = search.view

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Support Tickets</h1>
          <p className="ka-page-header-subtitle">
            {counts.open} open · {counts.inProgress} in progress ·{' '}
            {counts.waiting} waiting on customer · {counts.unassigned}{' '}
            unassigned
          </p>
        </div>
        <div className="ka-page-header-actions">
          <button type="button" className="ka-btn ka-btn--primary" disabled>
            <Plus size={14} aria-hidden="true" /> New Internal Ticket
          </button>
        </div>
      </header>

      <div className="ka-filter-strip">
        <div className="ka-filter-group">
          <span className="ka-filter-group-label">Status</span>
          <FilterChip
            active={!search.status}
            onClick={() => setStatusFilter(undefined)}
          >
            All
          </FilterChip>
          <FilterChip
            active={search.status === 'open'}
            onClick={() => setStatusFilter('open')}
          >
            Open
          </FilterChip>
          <FilterChip
            active={search.status === 'in_progress'}
            onClick={() => setStatusFilter('in_progress')}
          >
            In progress
          </FilterChip>
          <FilterChip
            active={search.status === 'waiting_on_customer'}
            onClick={() => setStatusFilter('waiting_on_customer')}
          >
            Waiting
          </FilterChip>
          <FilterChip
            active={search.status === 'resolved'}
            onClick={() => setStatusFilter('resolved')}
          >
            Resolved
          </FilterChip>
        </div>
        <div className="ka-filter-group">
          <span className="ka-filter-group-label">Priority</span>
          <FilterChip
            active={!search.priority}
            onClick={() => setPriorityFilter(undefined)}
          >
            Any
          </FilterChip>
          <FilterChip
            active={search.priority === 'urgent'}
            onClick={() => setPriorityFilter('urgent')}
          >
            Urgent
          </FilterChip>
          <FilterChip
            active={search.priority === 'high'}
            onClick={() => setPriorityFilter('high')}
          >
            High
          </FilterChip>
          <FilterChip
            active={search.priority === 'medium'}
            onClick={() => setPriorityFilter('medium')}
          >
            Normal
          </FilterChip>
          <FilterChip
            active={search.priority === 'low'}
            onClick={() => setPriorityFilter('low')}
          >
            Low
          </FilterChip>
        </div>
        <div className="ka-flex-spacer" />
        <ViewToggle value={view} onChange={setView} />
      </div>

      {view === 'table' ? (
        <>
          <TicketsTable
            tickets={tableRows}
            total={tableTotal}
            isLoading={tableQuery.isPending}
            isError={tableQuery.isError}
            {...(tableQuery.error instanceof Error
              ? { errorMessage: tableQuery.error.message }
              : {})}
            onBulkAction={handleBulkAction}
          />
          <DataTablePagination
            page={tablePagination?.page ?? search.page}
            limit={tablePagination?.limit ?? search.limit}
            total={tableTotal}
            isLoading={tableQuery.isPending}
            onChange={({ page, limit }) => setUrl({ page, limit })}
          />
        </>
      ) : (
        <TicketsKanban tickets={kanbanTickets} onMove={handleKanbanMove} />
      )}
    </div>
  )
}

interface FilterChipProps {
  readonly active: boolean
  readonly onClick: () => void
  readonly children: React.ReactNode
}

function FilterChip({
  active,
  onClick,
  children,
}: FilterChipProps): ReactElement {
  return (
    <button
      type="button"
      className={`ka-detail-chip${active ? ' is-active' : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  )
}
